/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片处理进度/结果弹窗组件（统一组件）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-10 10:35:07
 */

import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Progress, message } from 'antd'
import {
  PhotoItem,
  WatermarkExtractionStatus,
  useWatermarkBatchContext
} from '../../context/WatermarkBatchContext'
import { useWatermarkRemoval } from '../../hooks/useWatermarkRemoval'
import { useWatermarkBatchEditorTrack } from '../../hooks/useWatermarkBatchEditorTrack'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import { useAppDispatch, useGroupID } from '../../../../../app/hooks'
import { removeWatermarkDownload } from '../../../../../actions/photo'
import crown_svip_icon from '../../../../../images/crown-svip-icon.png'
import './WatermarkProcessModal.scss'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons'

export type ProcessModalMode = 'processing' | 'result'

interface WatermarkProcessModalProps {
  visible: boolean
  mode: ProcessModalMode
  title?: string
  description?: string // 处理中的描述文本（如果提供则使用，否则根据photoCount自动生成）
  photoCount?: number // 处理中的照片数量
  progress?: number // 处理中的进度（0-100），如果为undefined则自动增长
  timeout?: number // 超时时间（毫秒），如果不提供则根据photoCount计算
  successCount?: number // 成功数量（结果模式）
  successPhotos?: PhotoItem[] // 成功照片列表（结果模式）
  noChangesCount?: number // 无修改项数量（结果模式）
  noChangesPhotos?: PhotoItem[] // 无修改项照片列表（结果模式）
  timeoutCount?: number // 超时数量（结果模式）
  timeoutPhotos?: PhotoItem[] // 超时照片列表（结果模式）
  failedCount?: number // 失败数量（结果模式）
  failedPhotos?: PhotoItem[] // 失败的照片列表（结果模式）
  failureReason?: string // 失败原因（结果模式）
  surveyUrl?: string // 调研问卷链接（用于反馈入口）
  /** 点击「上传到文件夹」时回调，由父级负责 SVIP 校验并打开上传弹窗 */
  onUploadToFolderClick?: () => void
  onClose?: () => void
  onCancel?: () => void
}

const WatermarkProcessModal: React.FC<WatermarkProcessModalProps> = ({
  visible,
  mode,
  title,
  description,
  photoCount = 0,
  progress: externalProgress,
  timeout: externalTimeout,
  successCount = 0,
  successPhotos = [],
  noChangesCount = 0,
  noChangesPhotos = [],
  timeoutCount = 0,
  timeoutPhotos = [],
  failedCount = 0,
  failedPhotos = [],
  failureReason = '',
  surveyUrl,
  onUploadToFolderClick,
  onClose,
  onCancel
}) => {
  const [internalProgress, setInternalProgress] = useState(0)
  const progressIntervalRef = useRef<any>(null)
  const startTimeRef = useRef<number | null>(null)

  // 当前选中的照片索引，用于显示对应的失败原因
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
    null
  )

  // 下载相关状态
  const [downloading, setDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(false) // 标记是否已下载
  const [downloadProgress, setDownloadProgress] = useState({
    current: 0,
    total: 100
  })

  // 使用去水印hook
  const { removeWatermarks } = useWatermarkRemoval()
  const { buildThumbUrl, buildProcessedUrl } = useImageProcessUrl()

  const THUMB_SIZE = 80
  const PREVIEW_WIDTH = 1000
  const { trackWatermarkBatchEditor } = useWatermarkBatchEditorTrack()
  const dispatch = useAppDispatch()
  const groupID = useGroupID()
  const { updatePhotosStatus, openFullscreenPreview, watermarkCnt } =
    useWatermarkBatchContext()

  // 计算超时时间（毫秒）
  const calculateTimeout = (): number => {
    if (externalTimeout !== undefined) {
      return externalTimeout
    }
    // 根据照片数量计算超时时间：1-100张60秒，101-200张120秒
    if (photoCount > 0) {
      return Math.ceil(photoCount / 100) * 60 * 1000
    }
    // 默认60秒
    return 60 * 1000
  }

  // 处理进度自动增长逻辑（基于超时时间）
  useEffect(() => {
    if (visible && mode === 'processing' && externalProgress === undefined) {
      // 记录开始时间
      startTimeRef.current = Date.now()
      setInternalProgress(1) // 从1%开始

      // 清除之前的定时器
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
      }

      const timeoutMs = calculateTimeout()

      // 创建进度更新定时器（每200ms更新一次）
      progressIntervalRef.current = setInterval(() => {
        if (startTimeRef.current === null) {
          startTimeRef.current = Date.now()
        }

        const elapsed = Date.now() - startTimeRef.current
        // 计算进度百分比：基于经过的时间占超时时间的比例
        // 使用平滑的曲线函数，让进度条在接近超时时间时增长变慢
        // 进度范围：1% - 95%（永远不达到100%，直到任务完成）
        const progressRatio = Math.min(elapsed / timeoutMs, 0.95)

        // 使用缓动函数 easeOutCubic，让进度条增长更平滑
        // 公式：1 - (1 - t)^3，让进度条前期增长快，后期增长慢
        const easedProgress = 1 - Math.pow(1 - progressRatio, 3)

        // 将进度映射到 1% - 95% 范围
        // 超时时间越长，达到相同进度百分比需要的时间越长，因此进度条增长越慢
        const newProgress = Math.min(1 + easedProgress * 94, 95)

        setInternalProgress(newProgress)
      }, 200)
    } else {
      // 弹窗关闭时清除定时器和重置开始时间
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
      startTimeRef.current = null
    }

    // 清理函数
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
      startTimeRef.current = null
    }
  }, [visible, mode, externalProgress, externalTimeout, photoCount])

  // 弹窗打开时，默认选中第一张失败照片
  useEffect(() => {
    if (visible && mode === 'result') {
      if (failedPhotos.length > 0) {
        setSelectedPhotoIndex(0)
      } else {
        setSelectedPhotoIndex(null)
      }
    }
  }, [visible, mode, failedPhotos.length])

  // 弹窗关闭时重置下载状态
  useEffect(() => {
    if (!visible) {
      setDownloaded(false)
      setDownloading(false)
      setDownloadProgress({ current: 0, total: 100 })
    }
  }, [visible])

  // 获取当前显示的进度
  const getCurrentProgress = (): number => {
    if (mode === 'result') {
      return 100
    }
    return externalProgress !== undefined ? externalProgress : internalProgress
  }

  // 获取当前显示的失败原因
  const getCurrentFailureReason = (): string => {
    if (selectedPhotoIndex !== null && failedPhotos[selectedPhotoIndex]) {
      const selectedPhoto = failedPhotos[selectedPhotoIndex]
      return selectedPhoto.errorMsg || failureReason || '处理失败，请重试'
    }
    if (failureReason) {
      return failureReason
    }
    if (failedPhotos.length > 0) {
      return '点击失败的图片查看具体原因'
    }
    return failedCount > 0 ? '处理失败，请重试' : ''
  }

  // 处理照片点击
  const handlePhotoClick = (index: number) => {
    if (selectedPhotoIndex === index) {
      setSelectedPhotoIndex(null)
    } else {
      setSelectedPhotoIndex(index)
    }
  }

  // 处理成功照片点击，打开全屏预览（由根 index 挂载的 FullscreenPreviewModal 展示）
  const handleSuccessPhotoClick = (index: number, photos: PhotoItem[]) => {
    const imageList = photos
      .map((photo) =>
        buildProcessedUrl(
          photo.aiURL || photo.smallURL || photo.url || '',
          PREVIEW_WIDTH,
          85
        )
      )
      .filter(Boolean)
    openFullscreenPreview({
      imageList,
      index,
      photoType: 'success',
      failedPhotos: []
    })
  }

  // 处理失败照片点击，打开全屏预览
  const handleFailedPhotoClick = (index: number, photos: PhotoItem[]) => {
    const imageList = photos
      .map((photo) =>
        buildProcessedUrl(photo.smallURL || photo.url || '', PREVIEW_WIDTH, 85)
      )
      .filter(Boolean)
    openFullscreenPreview({
      imageList,
      index,
      photoType: 'failed',
      failedPhotos: photos
    })
    // 同时设置选中状态，以便显示失败原因
    setSelectedPhotoIndex(index)
  }

  // 下载已编辑成功照片
  const handleDownload = async () => {
    if (downloading || successPhotos.length === 0) {
      return
    }

    setDownloading(true)
    setDownloaded(false) // 重新下载时重置已下载状态

    try {
      // 获取已编辑成功照片的URL列表（使用aiURL）
      const photoUrls = successPhotos
        .map((photo) => photo.aiURL)
        .filter(Boolean)

      if (photoUrls.length === 0) {
        message.warning('没有可下载的照片')
        setDownloading(false)
        return
      }

      // 重置进度
      setDownloadProgress({ current: 0, total: 100 })

      // 调用去水印接口获取ZIP文件URL
      const zipUrl = await removeWatermarks(photoUrls, (current, total) => {
        // 更新下载进度
        setDownloadProgress({ current, total })
      })

      if (!zipUrl) {
        message.error('获取去水印照片失败')
        setDownloading(false)
        return
      }

      // 下载ZIP文件
      try {
        const response = await fetch(zipUrl)
        if (!response.ok) {
          throw new Error(`下载失败: ${response.statusText}`)
        }

        const blob = await response.blob()

        // 生成ZIP文件名（使用日期时间）
        const now = new Date()
        const dateTimeStr = `${now.getFullYear()}${String(
          now.getMonth() + 1
        ).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(
          now.getHours()
        ).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(
          now.getSeconds()
        ).padStart(2, '0')}`
        const zipFileName = `批量编辑水印照片_${dateTimeStr}.zip`

        // 创建下载链接
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = zipFileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        // 记录去水印次数
        if (groupID) {
          try {
            await dispatch(
              removeWatermarkDownload({
                groupID: groupID,
                removeType: 1, // 1 表示 ai去水印
                watermarkCount: successPhotos.length
              })
            )
          } catch (error) {
            console.error('记录去水印次数失败:', error)
          }
        }

        // 设置进度为完成并标记已下载
        setDownloadProgress({ current: 100, total: 100 })
        setDownloaded(true)

        // 更新所有成功照片的下载状态
        // 为每张照片创建更新对象，保持原有状态不变，只更新 isDownloaded
        const photosToUpdate = successPhotos.map((photo) => ({
          ...photo,
          isDownloaded: true
        }))
        // 按状态分组，确保每张照片使用其当前状态进行更新
        // 这样可以避免状态转换限制，同时更新 isDownloaded 字段
        const photosByStatus = new Map<WatermarkExtractionStatus, PhotoItem[]>()
        photosToUpdate.forEach((photo) => {
          const status =
            photo.watermarkStatus || WatermarkExtractionStatus.EDIT_COMPLETED
          if (!photosByStatus.has(status)) {
            photosByStatus.set(status, [])
          }
          photosByStatus.get(status)!.push(photo)
        })
        // 按状态分组批量更新
        photosByStatus.forEach((photos, status) => {
          updatePhotosStatus(photos, status)
        })

        message.success(`成功下载${successPhotos.length}张照片的ZIP文件`)
      } catch (error) {
        console.error('下载ZIP文件失败:', error)
        message.error('下载ZIP文件失败，请稍后重试')
        // 重置进度和下载状态
        setDownloadProgress({ current: 0, total: 100 })
        setDownloaded(false)
      }
    } catch (error) {
      console.error('下载照片失败:', error)
      message.error('下载照片失败，请稍后重试')
      // 重置进度和下载状态
      setDownloadProgress({ current: 0, total: 100 })
      setDownloaded(false)
    } finally {
      setDownloading(false)
    }
  }

  // 获取弹窗标题
  const getModalTitle = (): string => {
    if (title) return title
    if (mode === 'processing') {
      // 处理中模式：在标题中显示照片数量和提示
      if (description) return description
      if (photoCount > 0) {
        return `${photoCount}张照片处理中,请耐心等待...`
      }
      return '正在处理您的照片，请稍候...'
    }
    return '照片处理完成'
  }

  const currentProgress = getCurrentProgress()
  const isProcessing = mode === 'processing'

  return (
    <Modal
      open={visible}
      onCancel={isProcessing ? undefined : onClose || onCancel}
      closable={!isProcessing}
      mask={{ closable: false }}
      width={600}
      title={getModalTitle()}
      footer={
        !isProcessing ? (
          <Button
            type="primary"
            onClick={() => {
              // 关闭时打印当前选中的图片和失败图片的数据模型，便于排查
              const currentPhoto =
                selectedPhotoIndex !== null && failedPhotos[selectedPhotoIndex]
                  ? failedPhotos[selectedPhotoIndex]
                  : (failedPhotos[0] ?? null)
              console.log(
                '[WatermarkProcessModal] 关闭时 - 当前图片数据:',
                JSON.stringify(currentPhoto)
              )
              console.log(
                '[WatermarkProcessModal] 关闭时 - 失败图片列表数据:',
                JSON.stringify(failedPhotos)
              )
              onClose && onClose()
            }}
            className="confirmBtn"
          >
            继续处理
          </Button>
        ) : null
      }
      styles={{
        body: {
          padding: 0,
          maxHeight: 'calc(100vh - 140px)',
          overflowY: 'auto'
        }
      }}
      destroyOnHidden={false}
    >
      <div className={isProcessing ? 'processing' : ''}>
        <div className="modalBody">
          {/* 进度条 */}
          <div className="progressContainer">
            {isProcessing ? (
              // 处理中：使用自定义进度条
              <>
                <div className="progressBar">
                  <div
                    className="progressFill"
                    style={{ width: `${currentProgress}%` }}
                  />
                </div>
                <div className="progressText">
                  {Math.floor(currentProgress)}%
                </div>
              </>
            ) : (
              // 结果：使用 antd Progress 组件
              <>
                <Progress
                  percent={100}
                  strokeColor="#00b424"
                  showInfo={false}
                  className="progress"
                />
                <span className="progressText">100%</span>
              </>
            )}
          </div>

          {/* 处理中模式的提示内容 */}
          {isProcessing && (
            <div className="tips">
              <div className="tipItem info">
                仅支持「今日水印相机」拍摄的照片
              </div>
              <div className="tipItem warning">
                <SafetyCertificateOutlined className="tipIcon" />
                水印时间和地点不可修改
              </div>
            </div>
          )}

          {/* 结果模式：成功和失败消息 */}
          {!isProcessing && (
            <>
              {/* 成功消息 */}
              {successCount > 0 && (
                <div className="successSection">
                  <div className="successMessage">
                    <CheckCircleOutlined className="successIcon" />
                    <span>{successCount}张照片已编辑成功</span>
                  </div>
                  <div className="successTip">点击查看照片编辑结果是否正确</div>

                  {/* 成功照片缩略图 */}
                  {successPhotos.length > 0 && (
                    <div className="successPhotosContainer">
                      <div className="successPhotos">
                        {successPhotos.map((photo, index) => (
                          <div
                            key={photo.seq || index}
                            className="successPhotoItem"
                            onClick={() =>
                              handleSuccessPhotoClick(index, successPhotos)
                            }
                          >
                            <img
                              src={buildThumbUrl(photo.aiURL || '', THUMB_SIZE)}
                              alt={`成功照片 ${index + 1}`}
                              className="successPhotoImg"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 无修改项消息 */}
              {noChangesCount > 0 && (
                <div className="noChangesSection">
                  <div className="noChangesMessage">
                    <InfoCircleOutlined className="noChangesIcon" />
                    <span>{noChangesCount}张照片无修改项</span>
                  </div>

                  {/* 无修改项照片缩略图 */}
                  {noChangesPhotos.length > 0 && (
                    <div className="noChangesPhotosContainer">
                      <div className="noChangesPhotos">
                        {noChangesPhotos.map((photo, index) => (
                          <div
                            key={photo.seq || index}
                            className="noChangesPhotoItem"
                          >
                            <img
                              src={buildThumbUrl(
                                photo.smallURL || photo.url || '',
                                THUMB_SIZE
                              )}
                              alt={`无修改项照片 ${index + 1}`}
                              className="noChangesPhotoImg"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 超时消息 */}
              {timeoutCount > 0 && (
                <div className="timeoutSection">
                  <div className="timeoutMessage">
                    <ClockCircleOutlined className="timeoutIcon" />
                    <span>{timeoutCount}张照片编辑超时</span>
                  </div>

                  {/* 超时照片缩略图 */}
                  {timeoutPhotos.length > 0 && (
                    <div className="timeoutPhotosContainer">
                      <div className="timeoutPhotos">
                        {timeoutPhotos.map((photo, index) => (
                          <div
                            key={photo.seq || index}
                            className="timeoutPhotoItem"
                          >
                            <img
                              src={buildThumbUrl(
                                photo.smallURL || photo.url || '',
                                THUMB_SIZE
                              )}
                              alt={`超时照片 ${index + 1}`}
                              className="timeoutPhotoImg"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 失败消息 */}
              {failedCount > 0 && (
                <div className="failureSection">
                  <div className="failureMessage">
                    <ExclamationCircleOutlined className="failureIcon" />
                    <span>{failedCount}张照片处理失败</span>
                  </div>

                  <div className="successTip">点击查看照片编辑失败原因</div>

                  {/* 失败照片缩略图 */}
                  {failedPhotos.length > 0 && (
                    <div className="failedPhotosContainer">
                      <div className="failedPhotos">
                        {failedPhotos.map((photo, index) => {
                          const isSelected = selectedPhotoIndex === index
                          return (
                            <div
                              key={photo.seq || index}
                              className={`failedPhotoItem ${
                                isSelected ? 'selected' : ''
                              }`}
                              onClick={() =>
                                handleFailedPhotoClick(index, failedPhotos)
                              }
                            >
                              <img
                                src={buildThumbUrl(
                                  photo.smallURL || photo.url || '',
                                  THUMB_SIZE
                                )}
                                alt={`失败照片 ${index + 1}`}
                                className="failedPhotoImg"
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default WatermarkProcessModal
